/* eslint-disable no-console */
// pls fix me
import { create } from 'zustand';
import { produce } from 'immer';
import { devtools } from 'zustand/middleware';
import {
  Design,
  Areas,
  Floorplan,
  InterAreaConnection,
  HighwayArea,
  SingularStationArea,
  Segment,
  LogicalSpace,
  VehicleType,
  Obstacle,
  LoadType,
  PoiGroup,
  PointOfInterest,
  GateMapping,
  VehicleLayout,
  PoiConnection,
  Station,
  JustInTimeRoutingConfig,
  Flow,
  ReferenceImage,
  MetaData,
  FlowStop,
  Templates,
  Group,
  UxOnly,
} from '@thive/fddm-types';

import { IdUtils } from '@/modules/common/utils';
import { FloorplanRevision } from '@/modules/localDevHelpers/FloorplanDesign';
import { DEVTOOLS_OPTIONS } from '@/modules/debug/constants/zustand';
import { CatalogActions, createCatalogSlice } from './catalogSlice';
import { intersect, isIntersect, tuplify } from '@/modules/common/helpers/array';
import {
  excludeAreasByIds,
  filterAreasByIds,
  isHighwayArea,
  isRackingArea,
  isSingularStationArea,
} from '../helpers';
import {
  areasToFlatCollection,
  excludeTemplatesByIds,
  filterTemplatesByIds,
  pushAreaToItsCollection,
  templatesToFlatCollection,
} from '../helpers/helpers';
import { Area, Template } from '../types';
import { createPreferencesSlice, PreferencesActions } from './preferencesSlice';
import { mergeStations, mergeVehicleLayouts } from '../helpers/floorplanMergeHelper';
import { logger } from '@common/logging/logger';
import { isAtLeast1 } from '../helpers/minItems';

type StoreActions = {
  initialize(revision: FloorplanRevision): void;
  reset(): void;
  transact(updater: (draft: FloorplanRevision) => void): void;
  getModel(): FloorplanRevision;
};

export type FloorplanDesignStore = FloorplanRevision &
  MetaDataActions &
  StoreActions &
  AreaActions &
  ObstacleActions &
  LogicalSpaceActions & {
    interAreaConnectionActions: InterAreaConnectionActions;
  } & CatalogActions &
  PreferencesActions &
  DesignActions &
  TemplateActions &
  GroupActions &
  FlowActions &
  FlowStopActions &
  JustInTimeActions &
  SetupActions &
  FloorplanActions &
  TracksActions &
  ThumbnailActions;

type MetaDataActions = {
  getMetaData(): MetaData;
  setMetaData(metaData: MetaData): void;
};

type DesignActions = {
  getDesign(): Design;
  setDesign(design: Design): void;
};

type ThumbnailActions = {
  setThumbnail(thumbnailBase64: UxOnly['thumbnail']): void;
};

type AreaActions = {
  addAreas(areas: Areas): void;
  getAreaById(id: string): Area | undefined;
  getAreaByAreaIdOrSegmentId(id: string): Area | undefined;
  getHighwayBySegmentId(id: string): HighwayArea | undefined;
  /**
   * @description search for a highway by id, silently for use inside of the store
   */
  getHighwayBySegmentIdSilently(id: string): HighwayArea | undefined;
  getAreaOrSegmentById(
    areaOrSegmentId: string,
  ):
    | { type: 'area'; entity: Exclude<Area, HighwayArea> }
    | { type: 'segment'; entity: Segment }
    | undefined;
  getAreaOrAreasInFlowStopById(areaOrFlowStopId: string): Area[] | undefined;
  /**
   * @description search for an area by id, silently for use inside of the store
   */
  getAreaByIdSilently(id: string): Area | undefined;
  getHighwaySegmentById(id: string): Segment | undefined;
  /**
   * @description search for a highway segment by id, silently for use inside of the store
   */
  getHighwaySegmentByIdSilently(id: string): Segment | undefined;
  getAllAreas(): Areas;
  hasAreas(): boolean;
  getAreasByIds(ids: string[]): Areas;
  updateAreas(areas: Areas): Areas;
  updateArea(area: Area): void;
  deleteAreas(ids: string[]): void;
};

interface InterAreaConnectionActions {
  // no
  getAll(): InterAreaConnection[];
  // no
  getBetween(areaOrSegmentIdA: string, areaOrSegmentIdB: string): InterAreaConnection | undefined;
  // no
  getRelatedTo(areaOrSegmentIds: string[]): InterAreaConnection[];
  // no
  set(interAreaConnections: InterAreaConnection[]): void;

  // maybe?
  updateBetween(
    areaOrSegmentIdA: string,
    areaOrSegmentIdB: string,
    callbackFn: (currentValue: InterAreaConnection) => InterAreaConnection,
  ): void;
  // maybe?
  updateRelatedTo(
    areaOrSegmentId: string,
    callbackFn: (currentValue: InterAreaConnection[]) => InterAreaConnection[],
  ): void;
  // maybe?
  update(connection: InterAreaConnection): void;
  // DONE
  create(interAreaConnections: InterAreaConnection[]): InterAreaConnection[];
  // DONE
  deleteByIds(ids: string[]): string[];

  // no
  getById(id: string): InterAreaConnection | undefined;

  // no
  getByIdSilently(id: string): InterAreaConnection | undefined;
}

type ObstacleActions = {
  addObstacles(obstacles: FloorplanRevision['design']['obstacles'][number][]): void;
  updateObstacle(obstacle: Obstacle): void;
  deleteObstacles(ids: string[]): void;
  getObstacleById(id: string): Obstacle | undefined;
  getObstacleByIdSilently(id: string): Obstacle | undefined;
  getObstaclesByIds(ids: string[]): Obstacle[];
  getObstacleBySegmentId(id: string): Obstacle | undefined;
  /**
   * @description search for an obstacle by segment id, silently for use inside of the store
   */
  getObstacleBySegmentIdSilently(id: string): Obstacle | undefined;
};

type LogicalSpaceActions = {
  addLogicalSpaces(logicalSpaces: FloorplanRevision['design']['logicalSpaces'][number][]): void;
  updateLogicalSpace(logicalSpace: LogicalSpace): void;
  deleteLogicalSpaces(ids: string[]): void;
  getLogicalSpaceById(id: string): LogicalSpace | undefined;
  getLogicalSpacesByIds(ids: string[]): LogicalSpace[];
};

type TemplateActions = {
  addTemplates(templates: Templates): void;
  getTemplateById(id: string): Template | undefined;
  getTemplateByIdSilently(id: string): Template | undefined;
  getTemplateByAreaId(id: string): Template | undefined;
  getTemplatesByIds(ids: string[]): Templates;
  getAreasInTemplate(id: string): Area[] | undefined;
  updateTemplate(template: Template): void;
  deleteTemplates(ids: string[]): void;
};

type GroupActions = {
  addGroups(groups: Group[]): void;
  getGroupById(id: string): Group | undefined;
  getGroupByIdSilently(id: string): Group | undefined;
  getGroupByAreaId(id: string): Group | undefined;
  getGroupsByAreaIds(ids: string[]): Group[];
  getGroupsByIds(ids: string[]): Group[];
  getAreasInGroup(id: string): Area[] | undefined;
  updateGroup(group: Group): void;
  deleteGroups(ids: string[]): void;
};

type FlowActions = {
  addFlow(flow: Flow): void;
  addFlows(flows: Flow[]): void;
  deleteFlows(ids: string[]): void;
  getRelatedFlows(areaIds: string[]): Flow[];
  updateFlow(flow: Flow): void;
  getFlows(): Flow[];
  getFlowById(id: string): Flow | undefined;
  getFlowsByAreaId(areaId: string): Flow[];
  getFlowByIdSilently(id: string): Flow | undefined;
  getFlowsByIds(ids: string[]): Flow[];
};

type FlowStopActions = {
  addFlowStops(flowStops: FlowStop[]): void;
  updateFlowStop(flowStop: FlowStop): void;
  deleteFlowStops(ids: string[]): void;
  getFlowStopById(id: string): FlowStop | undefined;
  getFlowStopByIdSilently(id: string): FlowStop | undefined;
  getFlowStopByAreaId(areaId: string): FlowStop | undefined;
  getRelatedFlowStop(areaId: string): FlowStop | undefined;
  getRelatedFlowStops(areaIds: string[]): FlowStop[];
  getFlowStopLoadHandlingType(id: string): SingularStationArea['loadHandlingType'] | undefined;
};

type JustInTimeActions = {
  setJitRoutingConfigs(configs: JustInTimeRoutingConfig[]): void;
  deleteJitRoutingConfigs(ids: string[]): void;
  addJitRoutingConfigs(configs: JustInTimeRoutingConfig[]): void;
  getJitRoutingConfigs(): JustInTimeRoutingConfig[];
  getJitRoutingConfigById(id: string): JustInTimeRoutingConfig | undefined;
  getJitRoutingConfigByIdSilently(id: string): JustInTimeRoutingConfig | undefined;
  getJitRoutingConfigsByIds(ids: string[]): JustInTimeRoutingConfig[];
  getRelatedJitRoutingConfigs(areaIds: string[]): JustInTimeRoutingConfig[];
  updateJitRoutingConfig(jitRoutingConfig: JustInTimeRoutingConfig): void;
};

type SetupActions = {
  addLoadTypes(loadTypes: LoadType[]): void;
  setLoadTypes(loadTypes: LoadType[]): void;
  deleteLoadTypes(ids: string[]): void;
  getLoadTypeById(id: string): LoadType | undefined;
  getLoadTypeByIdSilently(id: string): LoadType | undefined;
  getLoadTypes(): LoadType[];
  getLoadTypeIds(): string[];
  addVehicleTypes(vehicleTypes: VehicleType[]): void;
  setVehicleTypes(vehicleTypes: VehicleType[]): void;
  addVehicleType(vehicleType: VehicleType): void;
  updateVehicleType(vehicleType: VehicleType): void;
  getVehicleTypeById(id: string): VehicleType | undefined;
  getVehicleTypeByIdSilently(id: string): VehicleType | undefined;
  getVehicleTypes(): VehicleType[];
  deleteVehicleTypes(ids: string[]): void;
  getVehicleTypeIds(): string[];
  setReferenceImage(value: UxOnly['referenceImage']): void;
  updateReferenceImageOpacity(value: ReferenceImage['opacity']): void;
  deleteReferenceImage(): void;
  setPointCloudInputFileRef(value: UxOnly['pointCloudInputFileRef']): void;
  setPointCloudOutputFileRef(value: UxOnly['pointCloudOutputFileRef']): void;
};

type FloorplanActions = {
  // stations
  setStations(stations: Station[]): void;
  removeStations(ids: string[]): void;
  addStations(stations: Station[]): void;
  getStationsByAreaIds(areaIds: string[]): Station[];
  // vehicle layouts
  setVehicleLayouts(vehicleLayouts: VehicleLayout[]): void;
  deleteVehicleLayouts(ids: string[]): void;
  addVehicleLayout(vehicleLayout: VehicleLayout): void;
  getVehicleLayoutByVehicleTypeRef(vehicleTypeId: string): VehicleLayout | undefined;
  getVehicleLayoutByVehicleTypeRefSilently(vehicleTypeId: string): VehicleLayout | undefined;
  // pois
  getPois(): PointOfInterest[];
  getPoisByVehicleType(vehicleTypeId: string): PointOfInterest[];
  getPoisByAreaIds(areaIds: string[]): PointOfInterest[];
  addPois(vehicleTypeId: string, pois: PointOfInterest[]): void;
  removePois(vehicleTypeId: string, ids: string[]): void;
  // poi groups
  getPoiGroupById(id: string): PoiGroup | undefined;
  getPoiGroupByIdSilently(id: string): PoiGroup | undefined;
  addPoiGroup(vehicleTypeId: string, poiGroup: PoiGroup): void;
  removePoiGroups(vehicleTypeId: string, poiIds: string[]): void;
  updatePoiGroup(id: string, callbackFn: (currentValue: PoiGroup) => PoiGroup): void;
  deletePoiGroups(vehicleTypeId: string, ids: string[]): void;
  // gate mappings
  getGateMappings(poiIds: string[]): GateMapping[];
  removeGateMappings(vehicleTypeId: string, poiIds: string[]): void;
  addGateMappings(vehicleTypeId: string, gateMappings: GateMapping[]): void;
  // poi connections
  removePoiConnections(vehicleTypeId: string, poiIds: string[]): void;
  addPoiConnections(vehicleTypeId: string, poiConnections: PoiConnection[]): void;
  // batched apply (single set, returns ids that should be removed from the canvas)
  applyFloorplan(floorplan: Floorplan): { removedElementIds: string[] };
};

type TracksActions = {
  setTracks(tracks: FloorplanRevision['tracks']): void;
  getTracks(poiIds: string[]): FloorplanRevision['tracks'];
};

const INITIAL_STATE: FloorplanRevision = {
  metaData: {
    created: '',
    alternativeId: '',
    projectName: '',
    alternativeName: '',
    lastUpdated: '',
    lastUpdatedBy: '',
    projectId: '',
    version: 1,
    revisionId: '',
    state: 'DRAFT',
  },
  uxonly: {
    catalog: { trafficRules: { maxSpeeds: [], vehicleModes: [] }, fileReferences: [] },
    preferences: { additionalProperties: {} },
    groups: [],
    referenceImage: undefined,
    thumbnail: '',
    pointCloudInputFileRef: undefined,
    pointCloudOutputFileRef: undefined,
  },
  setup: {
    loadTypes: [],
    vehicleTypes: [],
  },
  design: {
    areas: {
      highwayAreas: [],
      rackingAreas: [],
      singularStationAreas: [],
      stationaryAreas: [],
      manualAreas: [],
    },
    interAreaConnections: [],
    obstacles: [],
    logicalSpaces: [],
    templates: {
      lassieMinis: [],
      lassies: [],
      shuttleRacks: [],
    },
    justInTimeRoutingConfigs: [],
    flows: { flows: [], flowStops: [], dependentFlows: [] },
  },
  floorplan: {
    stations: [],
    vehicleLayouts: [],
  },
  tracks: [],
};

const hasAnyAreas = (areas: Areas): boolean =>
  Object.values(areas).some(({ length }) => length > 0);

export const useFloorplanDesignStore = create<FloorplanDesignStore>()(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      // #region store actions

      initialize(revision: FloorplanRevision) {
        set(
          {
            ...revision,
            uxonly: {
              ...revision.uxonly,
              thumbnail: revision.uxonly.thumbnail ?? '',
            },
          },
          undefined,
          {
            type: 'initialize',
          },
        );
      },

      reset() {
        set(
          {
            ...INITIAL_STATE,
          },
          undefined,
          {
            type: 'reset',
          },
        );
      },

      transact(updater) {
        set(
          (state): Partial<FloorplanRevision> => produce(state, (draft) => updater(draft)),
          undefined,
          {
            type: 'transact',
          },
        );
      },

      getModel() {
        return {
          metaData: get().metaData,
          uxonly: get().uxonly,
          setup: get().setup,
          design: get().design,
          floorplan: get().floorplan,
          tracks: get().tracks,
        };
      },

      ...createCatalogSlice(set, get),
      ...createPreferencesSlice(set, get),

      // #endregion

      // #region area connection actions

      interAreaConnectionActions: {
        getAll() {
          return get().design.interAreaConnections;
        },

        getBetween(areaOrSegmentIdA, areaOrSegmentIdB) {
          const interAreaConnection = get().design.interAreaConnections.find(
            (connection) =>
              (connection.idA === areaOrSegmentIdA && connection.idB === areaOrSegmentIdB) ||
              (connection.idA === areaOrSegmentIdB && connection.idB === areaOrSegmentIdA),
          );

          if (!interAreaConnection) {
            logger.debug(
              `Could not find area connection between: entity with id ${areaOrSegmentIdA} and entity with areaOrSegmentIdB: ${areaOrSegmentIdB}`,
            );
            return undefined;
          }

          return interAreaConnection;
        },

        getRelatedTo(areaOrSegmentIds) {
          const areaOrSegmentIdsSet = new Set(areaOrSegmentIds);

          const interAreaConnections = get().design.interAreaConnections.filter(
            (connection) =>
              areaOrSegmentIdsSet.has(connection.idA) || areaOrSegmentIdsSet.has(connection.idB),
          );

          if (interAreaConnections.length === 0) {
            return [];
          }

          return interAreaConnections;
        },

        set(interAreaConnections) {
          set(
            (state): Partial<FloorplanRevision> => ({
              design: {
                ...state.design,
                interAreaConnections,
              },
            }),
            undefined,
            {
              type: 'interAreaConnections/set',
            },
          );
        },

        updateBetween(areaOrSegmentIdA, areaOrSegmentIdB, callbackFn) {
          const existingEntity = get().interAreaConnectionActions.getBetween(
            areaOrSegmentIdA,
            areaOrSegmentIdB,
          );

          if (!existingEntity) {
            // TODO: FDDM: track exception
            logger.warn(
              `Could not update inter-area-connection between area or segment with 
              idA ${areaOrSegmentIdA} and area or segment with idB ${areaOrSegmentIdB}. Not found.`,
            );
            return;
          }

          const newInterAreaConnection = callbackFn(existingEntity);
          set(
            produce((state: FloorplanDesignStore) => {
              const interAreaConnection = state.design.interAreaConnections.find(
                (connection) =>
                  (connection.idA === areaOrSegmentIdA && connection.idB === areaOrSegmentIdB) ||
                  (connection.idA === areaOrSegmentIdB && connection.idB === areaOrSegmentIdA),
              );

              if (!interAreaConnection) return;

              Object.assign(interAreaConnection, newInterAreaConnection);
            }),
            undefined,
            {
              type: 'interAreaConnections/updateBetween',
            },
          );
        },

        updateRelatedTo(areaOrSegmentId, callbackFn) {
          const interAreaConnections = get().interAreaConnectionActions.getRelatedTo([
            areaOrSegmentId,
          ]);
          const newInterAreaConnections = callbackFn(interAreaConnections);

          set(
            produce((state: FloorplanDesignStore) => {
              newInterAreaConnections.forEach((newInterAreaConnection) => {
                const existingEntity = state.design.interAreaConnections.find(
                  (connection) =>
                    connection.idA === newInterAreaConnection.idA &&
                    connection.idB === newInterAreaConnection.idB,
                );

                if (!existingEntity) return;

                Object.assign(existingEntity, newInterAreaConnection);
              });
            }),
            undefined,
            {
              type: 'interAreaConnections/updateRelatedTo',
            },
          );
        },

        update(con) {
          const connection = get().interAreaConnectionActions.getByIdSilently(con.id);

          if (!connection) {
            logger.error(`Could not update interAreaConnection with id: ${con.id}. Not found.`);
            return;
          }

          set(
            produce((state: FloorplanDesignStore) => {
              const { interAreaConnections } = state.design;
              const connection = interAreaConnections.find((c) => c.id === con.id);
              if (!connection) return;

              Object.assign(connection, con);
            }),
            undefined,
            {
              type: 'interAreaConnections/update',
            },
          );
        },

        create(newInterAreaConnections) {
          const toAdd: InterAreaConnection[] = [];
          const duplicates: InterAreaConnection[] = [];

          const idKeysExisting = new Set(
            get().design.interAreaConnections.map((item) =>
              IdUtils.toSortedIdKey(item.idA, item.idB),
            ),
          );
          newInterAreaConnections.forEach((item) => {
            const idKey = IdUtils.toSortedIdKey(item.idA, item.idB);
            if (idKeysExisting.has(idKey)) {
              duplicates.push(item);
            } else {
              toAdd.push(item);
              idKeysExisting.add(idKey);
            }
          });

          if (duplicates.length > 0) {
            // TODO: FDDM: track exception
            logger.error(
              'An interAreaConnection already exists between entities with following ids: ',
              duplicates.map((item) => `${item.idA} and ${item.idB}`),
            );
          }

          if (toAdd.length === 0) return [];

          set(
            (state): Partial<FloorplanRevision> => ({
              design: {
                ...state.design,
                interAreaConnections: [...state.design.interAreaConnections, ...toAdd],
              },
            }),
            undefined,
            {
              type: 'interAreaConnections/add',
            },
          );

          return toAdd;
        },

        deleteByIds(ids) {
          const idsNotYetDeleted: Set<string> = new Set(ids);
          const idsDeleted: string[] = [];
          const remaining: InterAreaConnection[] = [];
          const { interAreaConnections } = get().design;

          interAreaConnections.forEach((item) => {
            if (ids.includes(item.id)) {
              idsDeleted.push(item.id);
              idsNotYetDeleted.delete(item.id);
            } else {
              remaining.push(item);
            }
          });

          if (idsNotYetDeleted.size > 0) {
            // TODO: FDDM: track exception
            logger.error("Some interAreaConnections couldn't be deleted. Ids not found: ", [
              ...idsNotYetDeleted,
            ]);
          }

          if (interAreaConnections.length === remaining.length) return [];

          set(
            (state): Partial<FloorplanRevision> => ({
              design: {
                ...state.design,
                interAreaConnections: remaining,
              },
            }),
            undefined,
            {
              type: 'interAreaConnections/delete/byIds',
            },
          );

          return idsDeleted;
        },

        getById(id) {
          const connection = get().interAreaConnectionActions.getByIdSilently(id);
          if (!connection) {
            logger.warn(`Could not find interAreaConnection with id: ${id}`);
            return;
          }

          return connection;
        },

        getByIdSilently(id) {
          return get().design.interAreaConnections.find((c) => c.id === id);
        },
      },

      // #endregion

      // #region metadata actions

      getMetaData() {
        return get().metaData;
      },

      setMetaData(metaData: MetaData) {
        set(
          {
            metaData,
          },
          undefined,
          {
            type: 'metaData/set',
          },
        );
      },

      // #endregion

      // #region area actions

      getDesign() {
        return get().design;
      },

      setDesign(design: Design) {
        set(
          {
            design,
          },
          undefined,
          {
            type: 'design/set',
          },
        );
      },

      // #endregion

      // #region area actions

      addAreas(newAreas) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              areas: {
                highwayAreas: [...state.design.areas.highwayAreas, ...newAreas.highwayAreas],
                singularStationAreas: [
                  ...state.design.areas.singularStationAreas,
                  ...newAreas.singularStationAreas,
                ],
                rackingAreas: [...state.design.areas.rackingAreas, ...newAreas.rackingAreas],
                stationaryAreas: [
                  ...state.design.areas.stationaryAreas,
                  ...newAreas.stationaryAreas,
                ],
                manualAreas: [...state.design.areas.manualAreas, ...newAreas.manualAreas],
              },
            },
          }),
          undefined,
          {
            type: 'areas/add',
          },
        );
      },

      getAreaById(id) {
        const area = get().getAreaByIdSilently(id);
        if (!area) {
          return;
        }

        return area;
      },

      getAreaByAreaIdOrSegmentId(id) {
        const area = get().getAreaByIdSilently(id);
        if (area) {
          return area;
        }

        const highwayArea = get().getHighwayBySegmentIdSilently(id);
        if (highwayArea) {
          return highwayArea;
        }

        return undefined;
      },

      getHighwayBySegmentId(id) {
        const highwayArea = get().getHighwayBySegmentIdSilently(id);

        if (!highwayArea) {
          logger.warn(`Could not find highway area by segment id: ${id}`);
          return;
        }

        return highwayArea;
      },

      getHighwayBySegmentIdSilently(id) {
        const { highwayAreas } = get().design.areas;
        const highwayArea = highwayAreas.find((highway) =>
          highway.segments.some((segment) => segment.id === id),
        );

        return highwayArea;
      },

      getAreaByIdSilently(id) {
        const { highwayAreas, rackingAreas, singularStationAreas, stationaryAreas, manualAreas } =
          get().design.areas;

        const area: Area | undefined = [
          ...highwayAreas,
          ...rackingAreas,
          ...singularStationAreas,
          ...stationaryAreas,
          ...manualAreas,
        ].find((area) => area.id === id);

        return area;
      },

      getHighwaySegmentById(id) {
        const segment = get().getHighwaySegmentByIdSilently(id);

        if (!segment) {
          // TODO: FDDM: track exception
          logger.warn(`Could not find segment with id: ${id}`);
          return;
        }

        return segment;
      },

      getHighwaySegmentByIdSilently(id) {
        const { highwayAreas } = get().design.areas;
        // eslint-disable-next-line no-restricted-syntax
        for (const hw of highwayAreas) {
          const segment = hw.segments.find((seg) => seg.id === id);
          if (segment) {
            return segment;
          }
        }

        return undefined;
      },

      getAreaOrSegmentById(areaOrSegmentId) {
        const area = get().getAreaByIdSilently(areaOrSegmentId);

        if (area) {
          if (isHighwayArea(area)) {
            logger.warn(
              `Did not expect to find an interAreaConnection-end that is a highway area. 
              A connection to a highway segment is supposed to reference one of its segments, not the highway area itself`,
            );

            return;
          }

          return {
            type: 'area',
            entity: area,
          };
        }

        const segment = get().getHighwaySegmentByIdSilently(areaOrSegmentId);

        if (segment) {
          return {
            type: 'segment',
            entity: segment,
          };
        }

        logger.warn('No area or segment found with id: ', areaOrSegmentId);

        return undefined;
      },

      getAreaOrAreasInFlowStopById(areaOrFlowStopId: string) {
        const area = get().getAreaByIdSilently(areaOrFlowStopId);

        if (area) {
          return [area];
        }

        const flowStop = get().getFlowStopByIdSilently(areaOrFlowStopId);
        if (flowStop) {
          const areas = get().getAreasByIds(flowStop.areaIds);
          return areasToFlatCollection(areas);
        }

        return undefined;
      },

      getAreasByIds(ids) {
        return filterAreasByIds(get().design.areas, ids);
      },

      getAllAreas(): Areas {
        return get().design.areas;
      },

      hasAreas() {
        return hasAnyAreas(get().design.areas);
      },

      updateAreas(updated) {
        const { areas } = get().design;
        const areaMaps = {
          highwayAreas: new Map(areas.highwayAreas.map((item) => [item.id, item])),
          rackingAreas: new Map(areas.rackingAreas.map((item) => [item.id, item])),
          singularStationAreas: new Map(areas.singularStationAreas.map((item) => [item.id, item])),
          stationaryAreas: new Map(areas.stationaryAreas.map((item) => [item.id, item])),
          manualAreas: new Map(areas.manualAreas.map((item) => [item.id, item])),
        };
        const updatedAreas: Areas = {
          highwayAreas: [],
          rackingAreas: [],
          singularStationAreas: [],
          stationaryAreas: [],
          manualAreas: [],
        };

        updated.highwayAreas.forEach((area) => {
          const areaToUpdate = areaMaps.highwayAreas.get(area.id);
          if (!areaToUpdate) {
            logger.warn(`Could not update area with id: ${area.id}. Not found.`);
            return;
          }

          const updated = {
            ...areaToUpdate,
            ...area, // TODO: FDDM: support partials or provide callback function to limit the need of the caller knowing all details about the area to update (might be just the name that needs to be updated for example)
          };
          updatedAreas.highwayAreas.push(updated);
          areaMaps.highwayAreas.set(updated.id, updated);
        });
        updated.rackingAreas.forEach((area) => {
          const areaToUpdate = areaMaps.rackingAreas.get(area.id);
          if (!areaToUpdate) {
            // TODO: FDDM: track exception
            logger.warn(`Could not update area with id: ${area.id}. Not found.`);
            return;
          }

          const updated = {
            ...areaToUpdate,
            ...area, // TODO: support partials
          };
          updatedAreas.rackingAreas.push(updated);
          areaMaps.rackingAreas.set(updated.id, updated);
        });
        updated.singularStationAreas.forEach((area) => {
          const areaToUpdate = areaMaps.singularStationAreas.get(area.id);
          if (!areaToUpdate) {
            // TODO: FDDM: track exception
            logger.warn(`Could not update area with id: ${area.id}. Not found.`);
            return;
          }

          const updated = {
            ...areaToUpdate,
            ...area, // TODO: support partials
          };
          updatedAreas.singularStationAreas.push(updated);
          areaMaps.singularStationAreas.set(updated.id, updated);
        });
        updated.stationaryAreas.forEach((area) => {
          const areaToUpdate = areaMaps.stationaryAreas.get(area.id);
          if (!areaToUpdate) {
            // TODO: FDDM: track exception
            logger.warn(`Could not update area with id: ${area.id}. Not found.`);
            return;
          }

          const updated = {
            ...areaToUpdate,
            ...area, // TODO: support partials
          };
          updatedAreas.stationaryAreas.push(updated);
          areaMaps.stationaryAreas.set(updated.id, updated);
        });
        updated.manualAreas.forEach((area) => {
          const areaToUpdate = areaMaps.manualAreas.get(area.id);
          if (!areaToUpdate) {
            // TODO: FDDM: track exception
            logger.warn(`Could not update area with id: ${area.id}. Not found.`);
            return;
          }
          const updated = {
            ...areaToUpdate,
            ...area, // TODO: support partials
          };
          updatedAreas.manualAreas.push(updated);
          areaMaps.manualAreas.set(updated.id, updated);
        });

        if (!hasAnyAreas(updatedAreas)) return updatedAreas;

        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              areas: {
                highwayAreas: [...areaMaps.highwayAreas.values()],
                rackingAreas: [...areaMaps.rackingAreas.values()],
                singularStationAreas: [...areaMaps.singularStationAreas.values()],
                stationaryAreas: [...areaMaps.stationaryAreas.values()],
                manualAreas: [...areaMaps.manualAreas.values()],
              },
            },
          }),
          undefined,
          {
            type: 'areas/update',
          },
        );

        return updatedAreas;
      },

      updateArea(area: Area) {
        if (!get().getAreaByIdSilently(area.id)) {
          logger.warn(`Could not update area with id: ${area.id}. Not found.`);
          return;
        }

        set(
          produce((state: FloorplanDesignStore) => {
            const {
              highwayAreas,
              rackingAreas,
              singularStationAreas,
              stationaryAreas,
              manualAreas,
            } = state.design.areas;

            const target: Area | undefined = [
              ...highwayAreas,
              ...rackingAreas,
              ...singularStationAreas,
              ...stationaryAreas,
              ...manualAreas,
            ].find((target) => target.id === area.id);
            if (!target) return;

            Object.assign(target, area);
          }),
          undefined,
          {
            type: 'area/update',
          },
        );
      },

      deleteAreas(ids) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              areas: excludeAreasByIds(state.design.areas, ids),
            },
          }),
          undefined,
          {
            type: 'areas/delete/byIds',
          },
        );
      },

      // #endregion

      // #region obstacle actions

      addObstacles(obstacles) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              obstacles: [...state.design.obstacles, ...obstacles],
            },
          }),
          undefined,
          {
            type: 'obstacles/add',
          },
        );
      },

      updateObstacle(obs: Obstacle) {
        const obstacle = get().getObstacleByIdSilently(obs.id);

        if (!obstacle) {
          logger.error(`Could not update obstacle with id: ${obs.id}. Not found.`);
          return;
        }

        set(
          produce((state: FloorplanDesignStore) => {
            const { obstacles } = state.design;
            const obstacle = obstacles.find((o) => o.id === obs.id);
            if (!obstacle) return;

            Object.assign(obstacle, obs);
          }),
          undefined,
          {
            type: 'obstacle/update',
          },
        );
      },

      deleteObstacles(ids) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              obstacles: state.design.obstacles.filter((obstacle) => !ids.includes(obstacle.id)),
            },
          }),
          undefined,
          {
            type: 'obstacles/delete/byIds',
          },
        );
      },

      getObstacleById(id) {
        const obstacle = get().getObstacleByIdSilently(id);
        if (!obstacle) {
          logger.warn(`Could not find obstacle with id: ${id}`);
          return;
        }

        return obstacle;
      },

      getObstacleByIdSilently(id) {
        return get().design.obstacles.find((obstacle) => obstacle.id === id);
      },

      getObstaclesByIds(ids) {
        return get().design.obstacles.filter((obstacle) => ids.includes(obstacle.id));
      },

      getObstacleBySegmentId(id) {
        const obstacle = get().getObstacleBySegmentIdSilently(id);

        if (!obstacle) {
          logger.warn(`Could not find obstacle by segment id: ${id}`);
          return;
        }

        return obstacle;
      },

      getObstacleBySegmentIdSilently(id) {
        const { obstacles } = get().design;
        const obstacle = obstacles.find((obstacle) =>
          obstacle.segments.some((segment) => segment.id === id),
        );

        return obstacle;
      },

      // #endregion

      // #region logical space actions

      addLogicalSpaces(logicalSpaces) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              logicalSpaces: [...state.design.logicalSpaces, ...logicalSpaces],
            },
          }),
          undefined,
          {
            type: 'addLogicalSpaces/add',
          },
        );
      },

      deleteLogicalSpaces(ids) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              logicalSpaces: state.design.logicalSpaces.filter(
                (logicalSpace) => !ids.includes(logicalSpace.id),
              ),
            },
          }),
          undefined,
          {
            type: 'logicalSpaces/delete/byIds',
          },
        );
      },

      updateLogicalSpace(newLogicalSpace: LogicalSpace) {
        const logicalSpace = get().design.logicalSpaces.find((ls) => ls.id === newLogicalSpace.id);

        if (!logicalSpace) {
          logger.error(`Could not update logical space with id: ${newLogicalSpace.id}. Not found.`);
          return;
        }

        set(
          produce((state: FloorplanDesignStore) => {
            const { logicalSpaces } = state.design;

            const logicalSpace = logicalSpaces.find((ls) => ls.id === newLogicalSpace.id);
            if (!logicalSpace) return;

            Object.assign(logicalSpace, newLogicalSpace);
          }),
          undefined,
          {
            type: 'logicalSpace/update',
          },
        );
      },

      getLogicalSpaceById(id) {
        return get().design.logicalSpaces.find((ls) => ls.id === id);
      },

      getLogicalSpacesByIds(ids) {
        return get().design.logicalSpaces.filter((logicalSpace) => ids.includes(logicalSpace.id));
      },

      // #endregion

      // #region template actions

      addTemplates(templates) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              templates: {
                lassies: [...state.design.templates.lassies, ...templates.lassies],
                lassieMinis: [...state.design.templates.lassieMinis, ...templates.lassieMinis],
                shuttleRacks: [...state.design.templates.shuttleRacks, ...templates.shuttleRacks],
                hikStorages: [
                  ...(state.design.templates.hikStorages || []),
                  ...(templates.hikStorages || []),
                ],
              },
            },
          }),
          undefined,
          {
            type: 'groups/add',
          },
        );
      },

      getTemplateById(id) {
        const template = get().getTemplateByIdSilently(id);

        if (!template) {
          // TODO: FDDM: track exception
          logger.warn(`Could not find template with id: ${id}`);
          return;
        }

        return template;
      },

      getTemplateByIdSilently(id) {
        const allTemplates = templatesToFlatCollection(get().design.templates);
        const template = allTemplates.find((template) => template.group.id === id);

        return template;
      },

      getTemplateByAreaId(id) {
        const { lassies, lassieMinis, shuttleRacks, hikStorages } = get().design.templates;

        const allTemplates = [
          ...lassies,
          ...lassieMinis,
          ...shuttleRacks,
          ...(hikStorages || []),
        ].find((template) => template.group.areaIds.includes(id));

        if (!allTemplates) {
          logger.debug(`Could not find group with id: ${id}`);
          return undefined;
        }

        return allTemplates;
      },

      getTemplatesByIds(ids) {
        return filterTemplatesByIds(get().design.templates, ids);
      },

      getAreasInTemplate(id) {
        const template = get().getTemplateByIdSilently(id);

        if (!template) {
          logger.debug(`Could not get areas in template. Template with id '${id}' not found.`);
          return;
        }

        return areasToFlatCollection(filterAreasByIds(get().design.areas, template.group.areaIds));
      },

      updateTemplate(newTemplate: Template) {
        const { id } = newTemplate.group;
        if (!get().getTemplateByIdSilently(id)) {
          throw new Error(`Could not update template with id: ${id}. Not found.`);
        }

        set(
          produce((state: FloorplanDesignStore) => {
            const { lassies, lassieMinis, shuttleRacks, hikStorages } = state.design.templates;

            const templateToUpdate = [
              ...lassies,
              ...lassieMinis,
              ...shuttleRacks,
              ...(hikStorages || []),
            ].find((template) => template.group.id === id);
            if (!templateToUpdate) return;

            Object.assign(templateToUpdate, newTemplate);
          }),
          undefined,
          {
            type: 'template/update',
          },
        );
      },

      deleteTemplates(ids) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              templates: excludeTemplatesByIds(state.design.templates, ids),
            },
          }),
          undefined,
          {
            type: 'templates/delete/byIds',
          },
        );
      },

      // #endregion

      // #region group actions

      addGroups(groups) {
        set(
          (state): Partial<FloorplanRevision> => ({
            uxonly: {
              ...state.uxonly,
              groups: [...state.uxonly.groups, ...groups],
            },
          }),
          undefined,
          {
            type: 'groups/add',
          },
        );
      },

      getGroupById(id) {
        const group = get().getGroupByIdSilently(id);

        if (!group) {
          logger.warn(`Could not find group with id: ${id}`);
          return;
        }

        return group;
      },

      getGroupByIdSilently(id) {
        return get().uxonly.groups.find((group) => group.id === id);
      },

      getGroupByAreaId(id) {
        const { groups } = get().uxonly;

        const groupWithArea = groups.find((group) => group.areaIds.includes(id));

        if (!groupWithArea) {
          logger.debug(`Could not find group with area id: ${id}`);
          return undefined;
        }

        return groupWithArea;
      },

      getGroupsByIds(ids) {
        return get().uxonly.groups.filter((group) => ids.includes(group.id));
      },

      getGroupsByAreaIds(ids) {
        const idsSet = new Set(ids);
        return get().uxonly.groups.filter((group) =>
          group.areaIds.some((areaId) => idsSet.has(areaId)),
        );
      },

      getAreasInGroup(id) {
        const group = get().getGroupByIdSilently(id);

        if (!group) {
          logger.debug(`Could not get areas in group. Group with id '${id}' not found.`);
          return;
        }

        return areasToFlatCollection(filterAreasByIds(get().design.areas, group.areaIds));
      },

      updateGroup(newGroup: Group) {
        const { id } = newGroup;
        if (!get().getGroupByIdSilently(id)) {
          throw new Error(`Could not update group with id: ${id}. Not found.`);
        }

        set(
          produce((state: FloorplanDesignStore) => {
            const { groups } = state.uxonly;

            const groupToUpdate = groups.find((group) => group.id === id);
            if (!groupToUpdate) return;

            Object.assign(groupToUpdate, newGroup);
          }),
          undefined,
          {
            type: 'group/update',
          },
        );
      },

      deleteGroups(ids) {
        set(
          (state): Partial<FloorplanRevision> => ({
            uxonly: {
              ...state.uxonly,
              groups: state.uxonly.groups.filter((group) => !ids.includes(group.id)),
            },
          }),
          undefined,
          {
            type: 'groups/delete/byIds',
          },
        );
      },

      // #endregion

      // #region flow actions

      addFlow(newFlow) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              flows: {
                ...state.design.flows,
                flows: [...state.design.flows.flows, newFlow],
              },
            },
          }),
          undefined,
          {
            type: 'flow/add',
          },
        );
      },

      addFlows(flows) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              flows: {
                ...state.design.flows,
                flows: [...state.design.flows.flows, ...flows],
              },
            },
          }),
          undefined,
          {
            type: 'flows/add',
          },
        );
      },

      deleteFlows(ids) {
        const idsSet = new Set(ids);

        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              flows: {
                ...state.design.flows,
                flows: state.design.flows.flows.filter((item) => !idsSet.has(item.id)),
              },
            },
          }),
          undefined,
          {
            type: 'flows/delete/byIds',
          },
        );
      },

      getRelatedFlows(areaIds) {
        const relatedFlowStopIds = get()
          .getRelatedFlowStops(areaIds)
          .map((flowStop) => flowStop.id);
        const areaAndFlowStopIds = new Set([...areaIds, ...relatedFlowStopIds]);

        const flows = get().design.flows.flows.filter(
          (flow) =>
            areaAndFlowStopIds.has(flow.sourceAreaOrFlowStopId) ||
            areaAndFlowStopIds.has(flow.destinationAreaOrFlowStopId),
        );

        return flows;
      },

      updateFlow(flow: Flow) {
        if (!get().design.flows.flows.find((f) => f.id === flow.id)) {
          logger.debug(`Could not update flow with id: ${flow.id}. Not found.`);
          return;
        }

        set(
          produce((state: FloorplanDesignStore) => {
            const currentFlow = state.design.flows.flows.find((f) => f.id === flow.id);
            if (!currentFlow) return;

            Object.assign(currentFlow, flow);
          }),
          undefined,
          {
            type: 'flow/update',
          },
        );
      },

      getFlows() {
        return get().design.flows.flows;
      },

      getFlowsByAreaId(areaId) {
        return get().design.flows.flows.filter(
          (flow) =>
            flow.sourceAreaOrFlowStopId === areaId || flow.destinationAreaOrFlowStopId === areaId,
        );
      },

      getFlowById(id) {
        const flow = get().getFlowByIdSilently(id);
        if (!flow) {
          logger.warn(`Could not find flow with id: ${id}`);
          return;
        }

        return flow;
      },

      getFlowByIdSilently(id) {
        return get().design.flows.flows.find((flow) => flow.id === id);
      },

      getFlowsByIds(ids) {
        return get().design.flows.flows.filter((flow) => ids.includes(flow.id));
      },

      // #endregion

      // #region flow stop actions

      addFlowStops(flowStops) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              flows: {
                ...state.design.flows,
                flowStops: [...state.design.flows.flowStops, ...flowStops],
              },
            },
          }),
          undefined,
          {
            type: 'flowStops/add',
          },
        );
      },

      updateFlowStop(fs: FlowStop) {
        const flowStop = get().getFlowStopByIdSilently(fs.id);

        if (!flowStop) {
          logger.error(`Could not update flow stop with id: ${fs.id}. Not found.`);
          return;
        }

        set(
          produce((state: FloorplanDesignStore) => {
            const { flowStops } = state.design.flows;
            const flowStop = flowStops.find((flowStop) => flowStop.id === fs.id);
            if (!flowStop) return;

            Object.assign(flowStop, fs);
          }),
          undefined,
          {
            type: 'flowStop/update',
          },
        );
      },

      deleteFlowStops(ids) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              flows: {
                ...state.design.flows,
                flowStops: state.design.flows.flowStops.filter(
                  (flowStop) => !ids.includes(flowStop.id),
                ),
              },
            },
          }),
          undefined,
          {
            type: 'flowStops/delete/byIds',
          },
        );
      },

      getFlowStopByAreaId(areaId) {
        return get().design.flows.flowStops.find((fs) => fs.areaIds.includes(areaId));
      },

      getFlowStopById(id) {
        const flowStop = get().getFlowStopByIdSilently(id);

        if (!flowStop) {
          return;
        }

        return flowStop;
      },

      getFlowStopByIdSilently(id) {
        return get().design.flows.flowStops.find((flowStop) => flowStop.id === id);
      },

      getRelatedFlowStops(areaIds) {
        return get().design.flows.flowStops.filter((flowStop) =>
          areaIds.some((id) => flowStop.areaIds.includes(id)),
        );
      },

      getRelatedFlowStop(areaId) {
        return get().design.flows.flowStops.find((flowStop) => flowStop.areaIds.includes(areaId));
      },

      getFlowStopLoadHandlingType(id) {
        const flowStop = get().getFlowStopByIdSilently(id);

        if (!flowStop) {
          logger.debug(`Could not get loadHandlingType of flow stop with id '${id}'. Not found.`);
          return;
        }

        const children = flowStop.areaIds;
        const loadHandlingTypes = children?.flatMap((childId) => {
          const child = get().getAreaByIdSilently(childId);
          if (!child || !isSingularStationArea(child)) return undefined;
          return child?.loadHandlingType;
        });
        if (!loadHandlingTypes) return undefined;

        const allSame = loadHandlingTypes.every((val, i, arr) => val === arr[0]);
        if (allSame) return loadHandlingTypes[0];
        return undefined;
      },

      // #endregion

      // #region jit actions

      setJitRoutingConfigs(configs) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              justInTimeRoutingConfigs: configs,
            },
          }),
          undefined,
          {
            type: 'jitRoutingConfigs/set',
          },
        );
      },

      deleteJitRoutingConfigs(ids) {
        const idsSet = new Set(ids);

        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              justInTimeRoutingConfigs: state.design.justInTimeRoutingConfigs.filter(
                (item) => !idsSet.has(item.id),
              ),
            },
          }),
          undefined,
          {
            type: 'jitRoutingConfigs/delete/byIds',
          },
        );
      },

      addJitRoutingConfigs(configs) {
        set(
          (state): Partial<FloorplanRevision> => ({
            design: {
              ...state.design,
              justInTimeRoutingConfigs: [...state.design.justInTimeRoutingConfigs, ...configs],
            },
          }),
          undefined,
          {
            type: 'jitRoutingConfigs/add',
          },
        );
      },

      getJitRoutingConfigs() {
        return get().design.justInTimeRoutingConfigs;
      },

      getJitRoutingConfigById(id) {
        const config = get().getJitRoutingConfigByIdSilently(id);

        if (!config) {
          logger.warn(`Could not find config with id: ${id}`);
          return;
        }

        return config;
      },

      getJitRoutingConfigByIdSilently(id) {
        return get().design.justInTimeRoutingConfigs.find((config) => config.id === id);
      },

      getJitRoutingConfigsByIds(ids) {
        const idsSet = new Set(ids);
        return get().design.justInTimeRoutingConfigs.filter((config) => idsSet.has(config.id));
      },

      getRelatedJitRoutingConfigs(areaIds) {
        const areas = get().getAreasByIds(areaIds);
        const singularStationAreaIds = areas.singularStationAreas.map((area) => area.id);
        const stationaryAreaIds = areas.stationaryAreas.map((area) => area.id);
        const segmentIds = areas.highwayAreas.map(
          (highway) => IdUtils.destructureIdWithSegmentIndex(highway.segments[0].id).areaId,
        );

        return get().design.justInTimeRoutingConfigs.filter(
          (config) =>
            segmentIds.includes(
              IdUtils.destructureIdWithSegmentIndex(config.highwayAreaId).areaId,
            ) &&
            (isIntersect(config.targetAreaIds, singularStationAreaIds) ||
              isIntersect(config.waitAreaIds, stationaryAreaIds)),
        );
      },

      updateJitRoutingConfig(jitRC) {
        const jitRoutingConfig = get().getJitRoutingConfigByIdSilently(jitRC.id);

        if (!jitRoutingConfig) {
          logger.error(`Could not update jit routing config with id: ${jitRC.id}. Not found.`);
          return;
        }

        set(
          produce((state: FloorplanDesignStore) => {
            const { justInTimeRoutingConfigs } = state.design;
            const config = justInTimeRoutingConfigs.find((c) => c.id === jitRC.id);
            if (!config) return;

            Object.assign(config, jitRC);
          }),
          undefined,
          {
            type: 'jitRoutingConfig/update',
          },
        );
      },

      // #endregion

      // #region setup actions

      addLoadTypes(loadTypes) {
        set(
          (state): Partial<FloorplanRevision> => ({
            setup: {
              ...state.setup,
              loadTypes: [...state.setup.loadTypes, ...loadTypes],
            },
          }),
          undefined,
          {
            type: 'loadTypes/add',
          },
        );
      },

      setLoadTypes(loadTypes) {
        set(
          (state): Partial<FloorplanRevision> => ({
            setup: {
              ...state.setup,
              loadTypes,
            },
          }),
          undefined,
          {
            type: 'loadTypes/set',
          },
        );

        const updatedAreaIds: string[] = []; // Keep track of updated area ids, for PFG
        const areas = areasToFlatCollection(get().design.areas);

        const updatedAreas: Areas = {
          highwayAreas: [],
          rackingAreas: [],
          singularStationAreas: [],
          stationaryAreas: [],
          manualAreas: [],
        };

        areas.forEach((area) => {
          if (isRackingArea(area) && area.rackSpec.loadTypes[0].loadTypeId !== loadTypes[0].id) {
            updatedAreaIds.push(area.id);
            const newArea = {
              ...area,
              rackSpec: {
                ...area.rackSpec,
                loadTypes: tuplify([
                  {
                    ...area.rackSpec.loadTypes[0],
                    loadTypeRef: loadTypes[0].id,
                  },
                ]),
              },
            };
            pushAreaToItsCollection(updatedAreas, newArea);
          }
          if (isSingularStationArea(area) && area.stationSpec.loadTypeIds[0] !== loadTypes[0].id) {
            updatedAreaIds.push(area.id);
            const newArea = {
              ...area,
              stationSpec: {
                ...area.stationSpec,
                loadTypeIds: tuplify([loadTypes[0].id]),
              },
            };
            pushAreaToItsCollection(updatedAreas, newArea);
          }
        });

        get().updateAreas(updatedAreas);
      },

      deleteLoadTypes(ids) {
        const idsSet = new Set(ids);

        set(
          (state): Partial<FloorplanRevision> => ({
            setup: {
              ...state.setup,
              loadTypes: state.setup.loadTypes.filter((item) => !idsSet.has(item.id)),
            },
          }),
          undefined,
          {
            type: 'loadTypes/delete/byIds',
          },
        );
      },

      getLoadTypeById(id) {
        const loadType = get().getLoadTypeByIdSilently(id);
        if (!loadType) {
          logger.warn(`Could not find load type with id: ${id}`);
          return;
        }

        return loadType;
      },

      getLoadTypeByIdSilently(id) {
        return get().setup.loadTypes.find((lt) => lt.id === id);
      },

      getLoadTypes() {
        return get().setup.loadTypes;
      },

      getLoadTypeIds() {
        return get().setup.loadTypes.map((lt) => lt.id);
      },

      addVehicleTypes(vehicleTypes) {
        set(
          (state): Partial<FloorplanRevision> => {
            const nextVehicleTypesById = new Map(
              state.setup.vehicleTypes.map((vehicleType) => [vehicleType.id, vehicleType]),
            );

            vehicleTypes.forEach((vehicleType) => {
              nextVehicleTypesById.set(vehicleType.id, vehicleType);
            });

            return {
              setup: {
                ...state.setup,
                vehicleTypes: Array.from(nextVehicleTypesById.values()),
              },
            };
          },
          undefined,
          {
            type: 'vehicleTypes/add',
          },
        );
      },

      setVehicleTypes: (vehicleTypes) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            setup: {
              ...state.setup,
              vehicleTypes,
            },
          }),
          undefined,
          {
            type: 'vehicleTypes/set',
          },
        );
      },

      addVehicleType(vehicleType) {
        set(
          (state): Partial<FloorplanRevision> => ({
            setup: {
              ...state.setup,
              vehicleTypes: [...state.setup.vehicleTypes, vehicleType],
            },
          }),
          undefined,
          {
            type: 'vehicleType/add',
          },
        );
      },

      updateVehicleType(vt: VehicleType) {
        const vehicleType = get().getVehicleTypeByIdSilently(vt.id);

        if (!vehicleType) {
          logger.debug(`Could not update VehicleType with id: ${vt.id}. Not found.`);
          return;
        }

        set(
          produce((state: FloorplanDesignStore) => {
            const vehicleType = state.setup.vehicleTypes.find(
              (vehicleType) => vehicleType.id === vt.id,
            );
            if (!vehicleType) {
              logger.error(`Could not update VehicleType with id: ${vt.id}. Not found.`);
              return;
            }
            Object.assign(vehicleType, vt);
          }),
          undefined,
          {
            type: 'vehicleType/update',
          },
        );
      },

      getVehicleTypeById(id) {
        const vehicleType = get().getVehicleTypeByIdSilently(id);
        if (!vehicleType) {
          logger.warn(`Could not find vehicle type with id: ${id}`);
          return;
        }

        return vehicleType;
      },

      getVehicleTypeByIdSilently(id) {
        return get().setup.vehicleTypes.find((vt) => vt.id === id);
      },

      getVehicleTypes() {
        return get().setup.vehicleTypes;
      },

      deleteVehicleTypes(ids) {
        const idsSet = new Set(ids);
        const newVehicleTypes = get().setup.vehicleTypes.filter((item) => !idsSet.has(item.id));

        set(
          (state): Partial<FloorplanRevision> => ({
            setup: {
              ...state.setup,
              vehicleTypes: newVehicleTypes,
            },
          }),
          undefined,
          {
            type: 'vehicleTypes/delete/byIds',
          },
        );
      },

      getVehicleTypeIds() {
        return get().setup.vehicleTypes.map((vt) => vt.id);
      },

      setReferenceImage(value) {
        set(
          (state): Partial<FloorplanRevision> => ({
            uxonly: {
              ...state.uxonly,
              referenceImage: value,
            },
          }),
          undefined,
          {
            type: 'referenceImage/set',
          },
        );
      },

      updateReferenceImageOpacity(value) {
        const currentReferenceImage = get().uxonly.referenceImage;

        if (!currentReferenceImage) {
          logger.error(
            'Could not update referenceImage its opacity. ReferenceImage does not exist.',
          );
          return;
        }

        set(
          (state): Partial<FloorplanRevision> => ({
            uxonly: {
              ...state.uxonly,
              referenceImage: {
                ...currentReferenceImage,
                opacity: value,
              },
            },
          }),
          undefined,
          {
            type: 'referenceImage/opacity/set',
          },
        );
      },

      deleteReferenceImage() {
        set(
          (state): Partial<FloorplanRevision> => ({
            uxonly: {
              ...state.uxonly,
              referenceImage: undefined,
            },
          }),
          undefined,
          {
            type: 'referenceImage/delete',
          },
        );
      },

      setPointCloudInputFileRef(value): void {
        set(
          (state): Partial<FloorplanRevision> => ({
            uxonly: {
              ...state.uxonly,
              pointCloudInputFileRef: value,
            },
          }),
          undefined,
          {
            type: 'pointCloud/inputFileRef/set',
          },
        );
      },

      setPointCloudOutputFileRef(value): void {
        set(
          (state): Partial<FloorplanRevision> => ({
            uxonly: {
              ...state.uxonly,
              pointCloudOutputFileRef: value,
            },
          }),
          undefined,
          {
            type: 'pointCloud/outputFileRef/set',
          },
        );
      },

      // #endregion

      // #region Thumbnail actions

      setThumbnail: (thumbnailBase64: UxOnly['thumbnail']): void => {
        set(
          (state): Partial<FloorplanRevision> => ({
            uxonly: {
              ...state.uxonly,
              thumbnail: thumbnailBase64,
            },
          }),
          undefined,
          {
            type: 'thumbnail/set',
          },
        );
      },

      // #endregion

      // #region floorplan actions

      setStations: (stations) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              stations,
            },
          }),
          undefined,
          {
            type: 'stations/set',
          },
        );
      },

      addStations: (stations) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              stations: [...state.floorplan.stations, ...stations],
            },
          }),
          undefined,
          {
            type: 'stations/add',
          },
        );
      },

      removeStations: (ids) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              stations: state.floorplan.stations.filter((s) => !ids.includes(s.id)),
            },
          }),
          undefined,
          {
            type: 'stations/delete/byIds',
          },
        );
      },

      getStationsByAreaIds: (areaIds) => {
        const areaIdsSet = new Set(areaIds);
        return get().floorplan.stations.filter((station) =>
          station.areaIds.some((areaId) => areaIdsSet.has(areaId)),
        );
      },

      setVehicleLayouts: (vehicleLayouts) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts,
            },
          }),
          undefined,
          {
            type: 'vehicleLayouts/set',
          },
        );
      },

      deleteVehicleLayouts: (ids) => {
        const idsSet = new Set(ids);

        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: state.floorplan.vehicleLayouts.filter(
                (layout) => !idsSet.has(layout.vehicleTypeId),
              ),
            },
          }),
          undefined,
          {
            type: 'vehicleLayouts/delete/byIds',
          },
        );
      },

      addVehicleLayout: (vehicleLayout) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: [...state.floorplan.vehicleLayouts, vehicleLayout],
            },
          }),
          undefined,
          {
            type: 'vehicleLayout/add',
          },
        );
      },

      getVehicleLayoutByVehicleTypeRef(vehicleTypeId) {
        const vehicleLayout = get().getVehicleLayoutByVehicleTypeRefSilently(vehicleTypeId);

        if (!vehicleLayout) {
          logger.warn(`Could not find vehicle layout with vehicleTypeId: ${vehicleTypeId}`);
          return;
        }

        return vehicleLayout;
      },

      getVehicleLayoutByVehicleTypeRefSilently(vehicleTypeId) {
        const { vehicleLayouts } = get().floorplan;
        const vehicleLayout = vehicleLayouts.find((vl) => vl.vehicleTypeId === vehicleTypeId);
        return vehicleLayout;
      },

      addPois: (vehicleTypeId, pois) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: state.floorplan.vehicleLayouts.map((vl) =>
                vl.vehicleTypeId === vehicleTypeId ? { ...vl, pois: [...vl.pois, ...pois] } : vl,
              ),
            },
          }),
          undefined,
          {
            type: 'pois/add',
          },
        );
      },

      removePois: (vehicleTypeId, ids) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: state.floorplan.vehicleLayouts.map((vl) =>
                vl.vehicleTypeId === vehicleTypeId
                  ? { ...vl, pois: vl.pois.filter((poi) => !ids.includes(poi.id)) }
                  : vl,
              ),
            },
          }),
          undefined,
          {
            type: 'pois/remove',
          },
        );
      },

      addPoiGroup(vehicleTypeId, poiGroup) {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: state.floorplan.vehicleLayouts.map((vl) =>
                vl.vehicleTypeId === vehicleTypeId
                  ? { ...vl, poiGroups: [...vl.poiGroups, poiGroup] }
                  : vl,
              ),
            },
          }),
          undefined,
          {
            type: 'poiGroup/add',
          },
        );
      },

      removePoiGroups(vehicleTypeId, poiIds) {
        const poiIdSet = new Set(poiIds);

        set(
          produce((state: FloorplanDesignStore) => {
            const vehicleLayout = state.floorplan.vehicleLayouts.find(
              (layout) => layout.vehicleTypeId === vehicleTypeId,
            );

            if (!vehicleLayout) {
              return;
            }

            vehicleLayout.poiGroups = vehicleLayout.poiGroups.reduce<PoiGroup[]>(
              (result, poiGroup) => {
                const remainingPoiIds = poiGroup.poiIds.filter((poiId) => !poiIdSet.has(poiId));

                if (!isAtLeast1(remainingPoiIds)) {
                  return result;
                }

                result.push({
                  ...poiGroup,
                  poiIds: remainingPoiIds,
                });

                return result;
              },
              [],
            );
          }),
          undefined,
          {
            type: 'poiGroups/delete/byPoiIds',
          },
        );
      },

      getPois() {
        const { vehicleLayouts } = get().floorplan;
        return vehicleLayouts.flatMap((vehicleLayout) => vehicleLayout.pois);
      },

      getPoisByVehicleType(vehicleTypeId) {
        return get()
          .floorplan.vehicleLayouts.filter((vl) => vl.vehicleTypeId === vehicleTypeId)
          .map((vl) => vl.pois)
          .flat();
      },

      getPoisByAreaIds(areaIds) {
        const areaIdsSet = new Set(areaIds);
        return get()
          .floorplan.vehicleLayouts.flatMap((vehicleLayout) => vehicleLayout.pois)
          .filter((poi) => areaIdsSet.has(poi.areaId));
      },

      getPoiGroupById(id) {
        const poiGroup = get().getPoiGroupByIdSilently(id);

        if (!poiGroup) {
          logger.warn(`Could not find poi group with id: ${id}`);
          return;
        }

        return poiGroup;
      },

      getPoiGroupByIdSilently(id) {
        const { vehicleLayouts } = get().floorplan;

        const poiGroup = vehicleLayouts
          .flatMap((vehicleLayout) => vehicleLayout.poiGroups)
          .find((poiGroup) => poiGroup.id === id);
        return poiGroup;
      },

      updatePoiGroup(id, callbackFn) {
        const poiGroup = get().getPoiGroupByIdSilently(id);

        if (!poiGroup) {
          logger.debug(`Could not update poi group with id: ${id}. Not found.`);
          return;
        }

        const newPoiGroup = callbackFn(poiGroup);
        set(
          produce((state: FloorplanDesignStore) => {
            const poiGroup = state.floorplan.vehicleLayouts
              .flatMap((vl) => vl.poiGroups)
              .find((poiGroup) => poiGroup.id === id);

            if (!poiGroup) {
              logger.error(`Could not update poi group with id: ${id}. Not found.`);
              return;
            }
            Object.assign(poiGroup, newPoiGroup);
          }),
          undefined,
          {
            type: 'poiGroup/update',
          },
        );
      },

      deletePoiGroups: (vehicleTypeId, poiIds) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: state.floorplan.vehicleLayouts.map((vl) =>
                vl.vehicleTypeId === vehicleTypeId
                  ? {
                      ...vl,
                      poiGroups: vl.poiGroups.filter(
                        (poiGroup) => !poiGroup.poiIds.every((poiId) => poiIds.includes(poiId)),
                      ),
                    }
                  : vl,
              ),
            },
          }),
          undefined,
          {
            type: 'poiGroups/delete/byPoiIds',
          },
        );
      },

      getGateMappings(poiIds) {
        const { vehicleLayouts } = get().floorplan;
        const gateMappings = vehicleLayouts.flatMap((vehicleLayout) => vehicleLayout.gateMappings);

        const relevantGateMappings = gateMappings.filter(
          (mapping) =>
            isIntersect(mapping.sourcePoiIds, poiIds) || isIntersect(mapping.targetPoiIds, poiIds),
        );

        if (relevantGateMappings.length === 0) {
          // logger.error(`Could not find gate mapping with sourcePoiIds or targetPoiIds: ${poiIds}`);
        }

        return relevantGateMappings;
      },

      addGateMappings: (vehicleTypeId, gateMappings) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: state.floorplan.vehicleLayouts.map((vl) =>
                vl.vehicleTypeId === vehicleTypeId
                  ? { ...vl, gateMappings: [...vl.gateMappings, ...gateMappings] }
                  : vl,
              ),
            },
          }),
          undefined,
          {
            type: 'gateMappings/add',
          },
        );
      },

      removeGateMappings: (vehicleTypeId, poiIds) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: state.floorplan.vehicleLayouts.map((vl) =>
                vl.vehicleTypeId === vehicleTypeId
                  ? {
                      ...vl,
                      gateMappings: vl.gateMappings.filter(
                        (mapping) =>
                          !intersect(poiIds, mapping.sourcePoiIds) &&
                          !intersect(poiIds, mapping.targetPoiIds),
                      ),
                    }
                  : vl,
              ),
            },
          }),
          undefined,
          {
            type: 'gateMappings/delete',
          },
        );
      },

      addPoiConnections: (vehicleTypeId, poiConnections) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: state.floorplan.vehicleLayouts.map((vl) =>
                vl.vehicleTypeId === vehicleTypeId
                  ? { ...vl, poiConnections: [...vl.poiConnections, ...poiConnections] }
                  : vl,
              ),
            },
          }),
          undefined,
          {
            type: 'poiConnections/add',
          },
        );
      },

      removePoiConnections: (vehicleTypeId, poiIds) => {
        set(
          (state): Partial<FloorplanRevision> => ({
            floorplan: {
              ...state.floorplan,
              vehicleLayouts: state.floorplan.vehicleLayouts.map((vl) =>
                vl.vehicleTypeId === vehicleTypeId
                  ? {
                      ...vl,
                      poiConnections: vl.poiConnections.filter(
                        (poiConnection) =>
                          !poiIds.includes(poiConnection.startPoiId) &&
                          !poiIds.includes(poiConnection.endPoiId),
                      ),
                    }
                  : vl,
              ),
            },
          }),
          undefined,
          {
            type: 'poiConnections/delete/byIds',
          },
        );
      },

      applyFloorplan(incoming): { removedElementIds: string[] } {
        const removedElementIds: string[] = [];

        set(
          (state): Partial<FloorplanRevision> => {
            const existingFloorplan = state.floorplan;

            const stationsResult = mergeStations(
              existingFloorplan.stations,
              incoming.stations,
              areasToFlatCollection(state.design.areas),
            );
            const vehicleLayoutsResult = mergeVehicleLayouts(
              existingFloorplan.vehicleLayouts,
              incoming.vehicleLayouts,
            );

            removedElementIds.push(
              ...stationsResult.removedElementIds,
              ...vehicleLayoutsResult.removedElementIds,
            );

            return {
              floorplan: {
                ...existingFloorplan,
                stations: stationsResult.value,
                vehicleLayouts: vehicleLayoutsResult.value,
              },
            };
          },
          undefined,
          {
            type: 'floorplan/apply',
          },
        );

        return { removedElementIds };
      },

      // #endregion

      // #region tracks actions

      setTracks: (tracks) => {
        set(
          () => ({
            tracks,
          }),
          undefined,
          {
            type: 'tracks/set',
          },
        );
      },

      getTracks(poiIds) {
        const { tracks } = get();
        const { vehicleLayouts } = get().floorplan;

        const poiConnections = vehicleLayouts.flatMap(
          (vehicleLayout) => vehicleLayout.poiConnections,
        );
        const relevantPoiConnectionIds = poiConnections
          .filter(
            (poiConnection) =>
              poiIds.includes(poiConnection.startPoiId) || poiIds.includes(poiConnection.endPoiId),
          )
          .map((poiConnection) => poiConnection.id);

        const relevantTracks = tracks.filter((track) =>
          relevantPoiConnectionIds.includes(track.poiConnectionId),
        );

        if (relevantTracks.length === 0) {
          logger.error(`Could not find tracks related poiIds: ${poiIds}`);
        }

        return relevantTracks;
      },

      // #endregion
    }),
    { store: 'floorplanDesignStore', ...DEVTOOLS_OPTIONS },
  ),
);
